import { IconSearch, IconSpaces } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Input, Text } from '@fragments';
import { UserAvatar } from '@components/Community';

import { ALL_CATEGORIES, categoryLabel } from '../enums/communityCategories';

import OwnerOverview from './components/OwnerOverview';
import CommunitiesGrid from './components/CommunitiesGrid';
import HelpCard from './components/HelpCard';
import DownloadCard from './components/DownloadCard';
import { CardSkeleton } from './components/Skeletons';

import './styles.scss';

function greetingKey() {
    const hour = new Date().getHours();
    if (hour < 12) return 'home.greeting.morning';
    if (hour < 18) return 'home.greeting.afternoon';
    return 'home.greeting.evening';
}

export default function HomeView(props) {
    const { t } = useTranslation('dashboard');

    const {
        user,
        communitiesLoading,
        communityStats,
        isOwner,
        helpArticles,

        filteredCommunities,
        communitiesFiltered,
        communityCategories,
        communityCategory,
        setCommunityCategory,
        communitySearch,
        setCommunitySearch,
        communitySearchOpen,
        toggleCommunitySearch,
        closeCommunitySearchIfEmpty,
        clearCommunityFilters,
    } = props;

    const firstName = user?.username?.split(' ')[0];

    return (
        <div className="home">
            <header className="home__header">
                <div className="home__welcome">
                    <UserAvatar user={user} size={48} loading={!user} />
                    <div className="home__welcome-copy">
                        <Text.h3 weight={500} contrast={10} loading={!user}>
                            {firstName ? t(greetingKey(), { name: firstName }) : t('home.fallbackUser')}
                        </Text.h3>
                        <Text.span contrast={6}>{t('home.welcomeBack')}</Text.span>
                    </div>
                </div>
            </header>

            {communitiesLoading ? (
                <LoadingLayout />
            ) : (
                <>
                    {isOwner && <OwnerOverview {...props} />}

                    <section className="home__section home__bottom pt-5">
                        <div className="home__bottom-main">
                            <div className="home__section-heading">
                                <Text.caption weight={500} contrast={6} left={<IconSpaces />}>
                                    {t('common:entities.communities')}
                                </Text.caption>
                                <Text.h2
                                    grow
                                    weight={500}
                                    contrast={9}
                                    right={(
                                        /* The trigger *becomes* the field: one slot that
                                           widens from the 34px pill into the input. */
                                        <span
                                            className={`home__search${communitySearchOpen ? ' home__search--open' : ''}`}
                                        >
                                            {communitySearchOpen ? (
                                                <Input.Text
                                                    autoFocus
                                                    fullWidth
                                                    inputSize="small"
                                                    value={communitySearch}
                                                    onInput={setCommunitySearch}
                                                    placeholder={t('home.communities.search')}
                                                    start={<IconSearch />}
                                                    onBlur={closeCommunitySearchIfEmpty}
                                                    onKeyDown={e => { if (e.key === 'Escape') toggleCommunitySearch(); }}
                                                />
                                            ) : (
                                                <Button
                                                    variant="rounded"
                                                    color="overlay"
                                                    size="small"
                                                    icon={<IconSearch />}
                                                    tooltip={t('home.communities.search')}
                                                    onClick={toggleCommunitySearch}
                                                />
                                            )}
                                        </span>
                                    )}
                                >
                                    {t('home.communities.title')}
                                </Text.h2>
                            </div>

                            {/* Category chips. Categories come from the communities
                                themselves, so the bar only appears when there are any. */}
                            {communityCategories.length > 0 && (
                                <div className="home__filters">
                                    <Button
                                        variant="rounded"
                                        size="small"
                                        color={communityCategory === ALL_CATEGORIES ? 'inverse' : 'overlay'}
                                        onClick={() => setCommunityCategory(ALL_CATEGORIES)}
                                    >
                                        {t('home.communities.allCategories')}
                                    </Button>

                                    {communityCategories.map(category => (
                                        <Button
                                            key={category}
                                            variant="rounded"
                                            size="small"
                                            color={communityCategory === category ? 'inverse' : 'overlay'}
                                            onClick={() => setCommunityCategory(category)}
                                        >
                                            {categoryLabel(category)}
                                        </Button>
                                    ))}
                                </div>
                            )}

                            <CommunitiesGrid
                                communities={filteredCommunities}
                                stats={communityStats}
                                onOpen={props.openCommunity}
                                onShare={props.shareCommunity}
                                filtered={communitiesFiltered}
                                onClearFilters={clearCommunityFilters}
                            />
                        </div>

                        <aside className="home__bottom-aside">
                            <HelpCard articles={helpArticles} />
                            <DownloadCard />
                        </aside>
                    </section>
                </>
            )}
        </div>
    );
}

// Skeleton mirroring the loaded layout so the page doesn't jump when data lands.
function LoadingLayout() {
    return (
        <>
            {/* One placeholder for the unified owner panel. */}
            <CardSkeleton height={560} />

            <div className="home__bottom" aria-hidden="true">
                <div className="home__communities-grid">
                    {[0, 1].map(i => <CardSkeleton key={i} height={560} />)}
                </div>
                <div className="home__bottom-aside">
                    <CardSkeleton height={380} />
                    <CardSkeleton height={300} />
                </div>
            </div>
        </>
    );
}
